<template>
  <div class="site">

    <!-- PAGE HEADER -->
    <section class="page-header">
      <h1>{{ t('nav.cuidados') }}</h1>
      <p class="subtitle">{{ t('cuidados.subtitle') }}</p>
    </section>

    <!-- ── PETTING ── -->
    <section class="section light">
      <div class="container">
        <h2>{{ t('petting.title') }}</h2>
        <div class="tags">
          <span class="tag" v-for="(spot, i) in tm('petting.spots')" :key="i">{{ rt(spot) }}</span>
        </div>
      </div>
    </section>

    <!-- ── LOVES ── -->
    <section class="section cream">
      <div class="container two-col">
        <div class="col-text">
          <h2>{{ t('loves.title') }}</h2>
        </div>
        <ul class="icon-list">
          <li v-for="(item, i) in tm('loves.items')" :key="i">
            <span class="bullet">🐾</span>{{ rt(item) }}
          </li>
        </ul>
      </div>
    </section>

    <!-- ── FEARS ── -->
    <section class="section dark">
      <div class="container">
        <h2>{{ t('fears.title') }}</h2>
        <p class="desc">{{ t('fears.desc') }}</p>
        <div class="tags">
          <span class="tag tag-light" v-for="(item, i) in tm('fears.items')" :key="i">⚠️ {{ rt(item) }}</span>
        </div>

        <!-- Collage de miedos — sube tu imagen a public/collages/fears.jpg -->
        <div class="collage-slot">
          <img
            src="/collages/fears.jpg"
            alt="Lo que me da miedo"
            class="collage-img"
            @error="showFearPlaceholder = true"
            v-show="!showFearPlaceholder"
          />
          <div class="collage-placeholder" v-show="showFearPlaceholder">
            <span>📸</span>
            <p>Sube tu collage en <code>public/collages/fears.jpg</code></p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── CHASES ── -->
    <section class="section light">
      <div class="container">
        <h2>{{ t('chases.title') }}</h2>
        <p class="desc muted">{{ t('chases.desc') }}</p>
        <div class="tags">
          <span class="tag tag-warm" v-for="(item, i) in tm('chases.items')" :key="i">{{ rt(item) }}</span>
        </div>

        <!-- Collage de cosas que le gustan — sube tu imagen a public/collages/loves.jpg -->
        <div class="collage-slot">
          <img
            src="/collages/loves.jpg"
            alt="Lo que me hace salir corriendo"
            class="collage-img"
            @error="showLovesPlaceholder = true"
            v-show="!showLovesPlaceholder"
          />
          <div class="collage-placeholder" v-show="showLovesPlaceholder">
            <span>📸</span>
            <p>Sube tu collage en <code>public/collages/loves.jpg</code></p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── DIET ── -->
    <section class="section cream">
      <div class="container">
        <h2>{{ t('diet.title') }}</h2>
        <div class="diet-grid">
          <div class="diet-card">
            <h3>{{ t('diet.cant_eat_title') }}</h3>
            <ul class="plain-list">
              <li v-for="(item, i) in tm('diet.forbidden')" :key="i">{{ rt(item) }}</li>
            </ul>
          </div>
          <div class="diet-card">
            <h3>{{ t('diet.schedule_title') }}</h3>
            <p class="desc">{{ t('diet.schedule_desc') }}</p>
            <div class="steps">
              <div class="step" v-for="(step, i) in tm('diet.steps')" :key="i">
                <span class="step-time">{{ rt(step.time) }}</span>
                <p>{{ rt(step.instruction) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── COMMANDS ── -->
    <section class="section dark">
      <div class="container">
        <h2>{{ t('commands.title') }}</h2>
        <div class="commands-grid">
          <div class="command-card" v-for="(cmd, i) in tm('commands.items')" :key="i">
            <span class="command-word">{{ rt(cmd.word) }}</span>
            <p>{{ rt(cmd.how) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── VET ── -->
    <section class="section light">
      <div class="container center">
        <h2>{{ t('vet.title') }}</h2>
        <p class="vet-name">{{ t('vet.name') }}</p>
        <a href="https://wa.me/573242076694" target="_blank" class="btn btn-whatsapp">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          {{ t('vet.contact') }}
        </a>
      </div>
    </section>

    <footer class="footer">
      <p>{{ t('footer') }}</p>
    </footer>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'

export default {
  name: 'CuidadosView',
  setup() {
    const { t, tm, rt } = useI18n()
    const showFearPlaceholder = ref(false)
    const showLovesPlaceholder = ref(false)
    return { t, tm, rt, showFearPlaceholder, showLovesPlaceholder }
  }
}
</script>

<style scoped>
.page-header {
  padding: 160px 24px 80px;
  background: #1C1C1E;
  text-align: center;
}
.page-header h1 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 6vw, 5rem);
  color: white;
  font-weight: 700;
  margin-bottom: 16px;
}
.subtitle {
  font-size: 1rem;
  color: rgba(255,255,255,0.55);
  max-width: 480px;
  margin: 0 auto;
  line-height: 1.7;
}
.section { padding: 80px 24px; }
.section.dark  { background: #1C1C1E; }
.section.light { background: #FAFAF5; }
.section.cream { background: #F2EDE5; }
.container { max-width: 860px; margin: 0 auto; }
.container.center { text-align: center; }
.container.two-col { display: grid; grid-template-columns: 1fr 2fr; gap: 48px; align-items: start; }
.section.dark h2  { color: white; }
.section.light h2 { color: #1C1C1E; }
.section.cream h2 { color: #1C1C1E; }
h2 { font-family: 'Playfair Display', serif; font-size: clamp(1.8rem, 4vw, 2.8rem); font-weight: 700; line-height: 1.2; margin-bottom: 24px; }
h3 { font-family: 'Playfair Display', serif; font-size: 1.2rem; font-weight: 700; margin-bottom: 16px; color: #1C1C1E; }
.desc { font-size: 1rem; line-height: 1.7; margin-bottom: 32px; max-width: 600px; }
.section.dark .desc { color: rgba(255,255,255,0.7); }
.section.light .desc, .section.cream .desc { color: #6B6B6B; }
.desc.muted { color: #6B6B6B; }
.tags { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 8px; }
.tag { display: inline-block; padding: 8px 18px; border-radius: 99px; font-size: 0.9rem; background: #C8963E; color: white; }
.tag-light { background: transparent; border: 1px solid rgba(255,255,255,0.25); color: white; }
.tag-warm { background: #F2EDE5; color: #1C1C1E; }
.icon-list { list-style: none; display: flex; flex-direction: column; gap: 10px; }
.icon-list li { display: flex; align-items: baseline; gap: 10px; font-size: 0.95rem; line-height: 1.5; color: #2D2D2D; }
.bullet { flex-shrink: 0; }
.diet-grid { display: grid; grid-template-columns: 1fr 1.6fr; gap: 40px; align-items: start; }
.diet-card { background: white; border-radius: 20px; padding: 32px; box-shadow: 0 2px 16px rgba(0,0,0,0.05); }
.plain-list { list-style: none; display: flex; flex-direction: column; gap: 6px; }
.plain-list li { font-size: 0.9rem; color: #6B6B6B; padding-left: 12px; border-left: 2px solid #C8963E; }
.steps { display: flex; flex-direction: column; gap: 16px; margin-top: 8px; }
.step { display: flex; flex-direction: column; gap: 4px; }
.step-time { font-size: 0.75rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: #C8963E; }
.step p { font-size: 0.9rem; color: #6B6B6B; line-height: 1.6; }
.commands-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; margin-top: 8px; }
.command-card { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 16px; padding: 28px 24px; }
.command-word { display: block; font-family: 'Playfair Display', serif; font-size: 1.5rem; font-weight: 700; color: #E8B85A; margin-bottom: 10px; }
.command-card p { font-size: 0.9rem; color: rgba(255,255,255,0.65); line-height: 1.6; }
/* ── COLLAGE ── */
.collage-slot {
  margin-top: 36px;
  border-radius: 20px;
  overflow: hidden;
}
.collage-img {
  width: 100%;
  display: block;
  border-radius: 20px;
  object-fit: cover;
}
.collage-placeholder {
  background: rgba(255,255,255,0.06);
  border: 2px dashed rgba(255,255,255,0.2);
  border-radius: 20px;
  padding: 60px 40px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.section.light .collage-placeholder {
  background: #F2EDE5;
  border-color: #C8963E;
}
.collage-placeholder span { font-size: 2.5rem; }
.collage-placeholder p {
  font-size: 0.9rem;
  color: rgba(255,255,255,0.5);
  font-family: 'Inter', sans-serif;
}
.section.light .collage-placeholder p { color: #888; }
.collage-placeholder code {
  background: rgba(0,0,0,0.15);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
}

.vet-name { font-size: 1.1rem; color: #6B6B6B; margin-bottom: 28px; }
.btn { display: inline-flex; align-items: center; gap: 10px; padding: 14px 28px; border-radius: 99px; font-family: 'Inter', sans-serif; font-size: 0.95rem; font-weight: 500; border: none; cursor: pointer; text-decoration: none; transition: opacity 0.2s, transform 0.15s; }
.btn:hover { opacity: 0.88; transform: translateY(-1px); }
.btn-whatsapp { background: #25D366; color: white; }
.footer { background: #1C1C1E; color: rgba(255,255,255,0.35); text-align: center; padding: 32px 24px; font-size: 0.85rem; }

/* ── GALLERY ── */
.gallery-section { padding: 60px 40px; background: #F5F4EF; }
.gallery-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 16px;
  max-width: 960px;
  margin: 0 auto;
}
.gallery-card.tall { grid-row: span 2; }
.gallery-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.gallery-img {
  width: 100%;
  aspect-ratio: 4/3;
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
}
.gallery-card.tall .gallery-img { aspect-ratio: 3/4; flex: 1; }
.placeholder-img {
  background: #ebe9e2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
}
.gallery-caption { padding: 20px 24px; }
.gallery-caption h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 6px;
}
.gallery-caption p {
  font-family: 'Inter', sans-serif;
  font-size: 0.88rem;
  color: #777;
  line-height: 1.6;
}

@media (max-width: 640px) {
  .container.two-col { grid-template-columns: 1fr; gap: 24px; }
  .diet-grid { grid-template-columns: 1fr; }
  .gallery-section { padding: 40px 20px; }
  .gallery-grid { grid-template-columns: 1fr; }
  .gallery-card.tall { grid-row: span 1; }
}
</style>
